import { Award, History as HistoryIcon, Lock, ScrollText, Swords, Trophy, TrendingUp, UserPlus, type LucideIcon } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useLeagueHistory } from "@/modules/leagues/hooks/useLeagueAdmin";
import { SettingsSubHeader } from "@/modules/leagues/components/SettingsSubHeader";
import { SkeletonText } from "@/components/atoms/Skeleton";
import { EmptyState } from "@/components/organisms/EmptyState";
import type { HistoryEntryType } from "@/modules/leagues/types/admin";

// Precisa cobrir TODOS os tipos que qualquer parte do app grava na coleção
// `history` — ela é compartilhada com o Feed (duelo, gol, MVP da rodada),
// não é exclusiva desta tela. Bug real corrigido: essa lista ficou
// desatualizada quando o Feed passou a gravar tipos novos na mesma
// coleção, e `ICONS[tipo]` virava `undefined` — tentar renderizar
// `<undefined />` quebra a tela inteira (React lança exceção). Por isso
// também existe o fallback em `resolveIcon` abaixo: nenhum tipo futuro
// esquecido aqui deve voltar a quebrar a tela.
const ICONS: Record<HistoryEntryType, LucideIcon> = {
  rule_frozen: Lock,
  rule_updated: ScrollText,
  bonus: Award,
  penalty: Award,
  import: ScrollText,
  challenge_created: Swords,
  duel_accepted: Swords,
  duel_resolved: Trophy,
  round_mvp: Trophy,
  score_updated: TrendingUp,
  participant_joined: UserPlus,
};

function resolveIcon(type: string): LucideIcon {
  return ICONS[type as HistoryEntryType] ?? HistoryIcon; // tipo desconhecido = ícone genérico, nunca quebra
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function LeagueSettingsHistoryPage() {
  const { league } = useLeagueContext();
  const { data: history, isLoading } = useLeagueHistory(league.id);

  return (
    <div className="space-y-4">
      <SettingsSubHeader leagueId={league.id} title="Histórico de Pontuação" />
      <p className="text-xs text-muted-foreground">
        Registro append-only de toda alteração administrativa: congelamento de regras, bônus, descontos e
        importações — nunca é apagado.
      </p>

      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-3">
              <SkeletonText lines={2} />
            </div>
          ))}
        </div>
      )}

      {history && history.length === 0 && (
        <EmptyState icon={HistoryIcon} title="Nenhum registro ainda" description="Alterações administrativas aparecerão aqui." />
      )}

      {history && history.length > 0 && (
        <div className="space-y-2.5">
          {history.map((entry) => {
            const Icon = resolveIcon(entry.type);
            return (
              <div key={entry.id} className="flex items-start gap-3 rounded-xl border border-border bg-card p-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm">{entry.descricao}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {entry.administrador} · {formatDateTime(entry.createdAt)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
